// What a user is allowed to type into a code box.
//
// Every one of these fields previously accepted anything of any length — you
// could paste thirty digits into the login code and the form would happily
// submit it. The server rejected it, so nothing was ever *insecure*, but the
// user got a round trip and a generic "incorrect code" instead of being stopped
// at the keystroke.
//
// One module because there are five of these boxes across four screens
// (register, forgot-password, change-password, 2FA setup, 2FA login) and a rule
// implemented five times is a rule that will differ five ways.

/** Emailed OTPs and the TOTP setup code: exactly six digits, nothing else. */
export const OTP_LENGTH = 6;

/**
 * A recovery code is 12 characters shown as XXXX-XXXX-XXXX, so 14 with the
 * dashes. That is the longest legal input to the login code box.
 */
export const RECOVERY_INPUT_LENGTH = 14;

/**
 * Strip a value down to what an OTP field may hold: digits, capped at six.
 *
 * Non-digits are dropped rather than rejected so that pasting "123 456" or
 * "code: 123456" from an email does the obvious thing instead of failing.
 */
export function sanitizeOtp(value) {
  return String(value ?? '').replace(/\D/g, '').slice(0, OTP_LENGTH);
}

/**
 * The login code box, which takes EITHER a 6-digit TOTP or a 12-character
 * recovery code — the server tells them apart by shape, so the user never has
 * to say which they typed.
 *
 * Deliberately NOT "cap at 6 if it looks numeric": the recovery alphabet
 * includes the digits 2-9, so a code beginning "234567…" is indistinguishable
 * from a TOTP until the seventh character arrives — and capping at six would
 * make that code impossible to type. Cap at the longest legal input instead and
 * let `isCompleteCode` decide when the value is actually submittable.
 */
export function sanitizeLoginCode(value) {
  return String(value ?? '')
    .toUpperCase()
    .replace(/[^0-9A-Z-]/g, '')
    .slice(0, RECOVERY_INPUT_LENGTH);
}

/** Is this a plausible complete entry — six digits, or a full recovery code? */
export function isCompleteCode(value) {
  const v = String(value ?? '').trim();
  if (/^\d{6}$/.test(v)) return true;
  // Dashes are optional: people retype them from paper inconsistently.
  return v.replace(/-/g, '').length === 12 && /^[0-9A-Z-]+$/.test(v.toUpperCase());
}

/** Six digits and no more — for the fields that only ever take an emailed OTP. */
export const isCompleteOtp = (value) => /^\d{6}$/.test(String(value ?? '').trim());
